import { Section, Tray } from '@/components/atoms';
import { TrayScreen } from '@/components/atoms/Tray/interfaces/Tray';
import { ErrorAlert, InfoAlert } from '@/components/molecules';
import {
  TrayNavigationProvider,
  useTrayNavigation,
} from '@/components/providers/common/TrayNavigationProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { WalletInterface } from '@/interfaces/WalletInterface';
import { formatStringToFirstAndLast4Characters } from '@/utils/formatUtils';
import { generateWalletKey } from '@/utils/functionalUtils';
import { LABELS, WALLET } from '@/utils/strings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CoinbaseOnRampButton from '../CoinbaseOnRamp/CoinbaseOnRampButton';
import WalletsList from '../WalletsList/WalletsList';
import DepositToSelectedWalletDetails from './DepositToSelectedWalletDetails';
const DepositToWalletTrayContent = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [selectedWalletKey, setSelectedWalletKey] = useState<string | null>(
    null,
  );
  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(false);
  const [bottomDrawerContent, setBottomDrawerContent] = useState({
    headerTitle: '',
    bodyChildren: <></>,
  });
  const { navigateForward, navigateBack, goToScreen, currentScreenIndex } =
    useTrayNavigation();
  const { wallets, isUserAuthorized } = useWalletsContext();
  useEffect(() => {
    if (!isOpen) {
      setSelectedWalletKey(null);
      setIsBottomDrawerOpen(false);
    }
  }, [isOpen]);
  const handleClickOnHelpButtonForSelectWallet = () => {
    setBottomDrawerContent({
      headerTitle: 'Wallet',
      bodyChildren: (
        <div className="flex flex-col gap-sm">
          <InfoAlert
            title={WALLET.DESCRIPTION_TITLE}
            description={WALLET.DESCRIPTION}
          />
          <InfoAlert
            title={WALLET.TRANSFER_FROM_COINBASE_PAY_HELP_TITLE}
            description={WALLET.TRANSFER_FROM_COINBASE_PAY_HELP_DESCRIPTION}
          />
          <InfoAlert
            title={WALLET.TRANSFER_FROM_EXTERNAL_WALLET_HELP_TITLE}
            description={WALLET.TRANSFER_FROM_EXTERNAL_WALLET_HELP_DESCRIPTION}
          />
        </div>
      ),
    });
    setIsBottomDrawerOpen(true);
  };
  const handleSelectWallet = useCallback(
    ({ address, blockchain }: WalletInterface) => {
      setSelectedWalletKey(generateWalletKey({ address, blockchain }));
      navigateForward();
    },
    [navigateForward],
  );
  const handleNavigateBack = useCallback(() => {
    if (currentScreenIndex === 1) {
      setSelectedWalletKey(null);
      navigateBack();
    }
  }, [currentScreenIndex, navigateBack]);
  const selectedWallet = selectedWalletKey
    ? wallets.find((wallet) => wallet.walletKey === selectedWalletKey)
    : null;
  const screens: TrayScreen[] = useMemo(
    () => [
      {
        title: 'Select a wallet',
        content: !isUserAuthorized ? (
          <ErrorAlert
            title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
            description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
          />
        ) : (
          <div className="flex flex-col items-center gap-sm">
            <Section
              headerTitle={LABELS.WALLETS.DEPOSIT.TRANSFER_FROM_COINBASE_TITLE}
              subHeader={LABELS.WALLETS.DEPOSIT.TRANSFER_FROM_COINBASE_SUBTITLE}
              body={<CoinbaseOnRampButton />}
            />
            <Section
              headerTitle={
                LABELS.WALLETS.DEPOSIT.TRANSFER_FROM_EXTERNAL_WALLET_TITLE
              }
              subHeader={
                LABELS.WALLETS.DEPOSIT.TRANSFER_FROM_EXTERNAL_WALLET_SUBTITLE
              }
              body={
                <WalletsList
                  onSelectWallet={handleSelectWallet}
                  showWalletAddressCopyButton={false}
                  showWalletAddress={true}
                  showWalletBlockchain
                />
              }
            />
          </div>
        ),
        rightButtons: isUserAuthorized
          ? [
              {
                intent: 'ghost',
                icon: 'question',
                onClick: handleClickOnHelpButtonForSelectWallet,
              },
            ]
          : [],
      },
      {
        title: `${selectedWallet?.blockchainDisplayName || 'Wallet'} ${formatStringToFirstAndLast4Characters(
          selectedWallet?.address || '',
        )}`,
        content: (
          <DepositToSelectedWalletDetails
            selectedWalletKey={selectedWalletKey}
          />
        ),
        onNavigateBack: handleNavigateBack,
      },
    ],
    [
      selectedWalletKey,
      selectedWallet?.address,
      selectedWallet?.blockchainDisplayName,
      handleNavigateBack,
      handleSelectWallet,
    ],
  );
  useEffect(() => {
    if (screens[currentScreenIndex]?.navigateTo !== undefined) {
      goToScreen(screens[currentScreenIndex].navigateTo, true);
    }
  }, [currentScreenIndex, goToScreen, screens]);
  return (
    <Tray
      isOpen={isOpen}
      onClose={onClose}
      screens={screens}
      bottomDrawer={{
        isOpen: isBottomDrawerOpen,
        showHeaderCloseButton: true,
        onClose: () => setIsBottomDrawerOpen(false),
        headerTitle: bottomDrawerContent.headerTitle,
        bodyChildren: bottomDrawerContent.bodyChildren,
        includeFooterCloseButton: false,
      }}
    />
  );
};
const DepositToWalletTray: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = (props) => (
  <TrayNavigationProvider>
    <DepositToWalletTrayContent {...props} />
  </TrayNavigationProvider>
);
export default DepositToWalletTray;
